// frontend/src/components/KPIStrip.tsx
// 8 кликабельных компактных KPI-карточек со sparklines.
// Активная (в hero) — синий border + glow.
import type { ChartPoint } from "../api/charts.js";
import { Sparkline } from "../charts/Sparkline.js";
import { METRICS, type MetricId } from "../charts/METRICS.js";

export interface KPIStripItem {
  id: MetricId;
  current: number | null;
  previous: number | null;
  sparkline: ChartPoint[];
}

export interface KPIStripProps {
  metrics: KPIStripItem[];
  activeMetricId: MetricId | null;
  onActivate: (id: MetricId) => void;
}

export function KPIStrip({ metrics, activeMetricId, onActivate }: KPIStripProps) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${metrics.length}, 1fr)`,
        gap: 10,
        marginBottom: 24,
      }}
    >
      {metrics.map((m) => (
        <KPICard
          key={m.id}
          item={m}
          active={activeMetricId === m.id}
          onClick={() => onActivate(m.id)}
        />
      ))}
    </div>
  );
}

function KPICard({
  item,
  active,
  onClick,
}: {
  item: KPIStripItem;
  active: boolean;
  onClick: () => void;
}) {
  const meta = METRICS[item.id];
  const delta = computeDelta(item.current, item.previous);
  const invert = ("invertGoodSign" in meta) && meta.invertGoodSign;
  const isGood = delta == null ? null : (delta >= 0) === !invert;
  const arrow = delta == null ? "—" : delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const deltaPct = delta == null
    ? "—"
    : `${(Math.abs(delta) * 100).toFixed(1).replace(".", ",")}%`;
  const deltaColor = isGood == null
    ? "var(--text-muted)"
    : isGood
      ? "var(--status-good-fg)"
      : "var(--status-bad-fg)";

  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--surface-card)",
        border: active ? "1px solid var(--brand-primary, #0EA5E9)" : "1px solid var(--border-default)",
        borderRadius: 8,
        padding: 12,
        boxShadow: active
          ? "0 0 0 3px rgba(14,165,233,0.15)"
          : "var(--shadow-card)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        cursor: "pointer",
        transition: "all 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.boxShadow = "var(--shadow-card-hover)";
          e.currentTarget.style.borderColor = "var(--border-strong)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.boxShadow = "var(--shadow-card)";
          e.currentTarget.style.borderColor = "var(--border-default)";
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: meta.seriesColor,
          opacity: 0.85,
        }}
      />
      <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{meta.label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
          color: "var(--text-primary)",
        }}
      >
        {meta.format(item.current)}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          color: deltaColor,
        }}
      >
        {arrow} {deltaPct}
      </div>
      {item.sparkline.length >= 2 && (
        <div style={{ marginTop: 4 }}>
          <Sparkline metricId={item.id} points={item.sparkline} height={24} />
        </div>
      )}
      {active && (
        <div
          style={{
            position: "absolute",
            bottom: 6,
            right: 8,
            fontSize: 9,
            color: "var(--brand-primary, #0EA5E9)",
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          ▲ в hero
        </div>
      )}
    </div>
  );
}

function computeDelta(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}
